import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import { ModelPath } from "@com.mgmtp.a12.base/base-model-api/lib/main/model";
import { ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Model, ModelSelectors } from "@com.mgmtp.a12.client/client-core/lib/core/model";
import IExternalEnumerationProvider, {
	EmptyExternalEnumerationProvider
} from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/services/external-enumeration-provider";
import { FormActivity } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { DocumentModel, DocumentServiceFactory } from "@com.mgmtp.a12.kernel/kernel-md-facade";

import { getFVManifest } from "../../../app/slices/fachverfahren/selectors";
import { assertTrue } from "../../../utilities/assert";
import { findAnnotations } from "../../../utilities/modelHelpers";
import {
	convertEntityInstancePathToString,
	entityInstancePathFromModelPath,
	getValueFromModelPath
} from "../../../utilities/pathHelpers";
import { isObject, isString } from "../../../utilities/typeGuardUtils";
import {
	EXTERNAL_ENUMERATION,
	EXTERNAL_ENUMERATION_SOURCE
} from "../../../utilities/variables/modulfRuntimeAnnotations";
import { LOAD_CONTACT_DATA } from "../../contactData/actions";
import { isContactData } from "../../contactData/types";
import type { ContactData } from "../../contactData/types";
import { extractConfigList } from "../../../utilities/platzhalterHelper";
import { formatContact } from "../../../utilities/contacts";

export { isContactData };
export type { ContactData };

/**
 * Hook that creates an external enumeration provider for the Process Form Engine.
 * The provider searches for the annotation {@value EXTERNAL_ENUMERATION} in the document model.
 * The value of this annotation determines the identifier for the external enumeration.
 *
 * @param activityId The activity ID to get data and document model from
 * @returns An external enumeration provider
 */
export function useExternalEnumerationProvider(activityId: string): IExternalEnumerationProvider {
	const dispatch = useDispatch();
	const fvId = useSelector(ActivitySelectors.activityPropById(activityId, a => a.descriptor.fachverfahren));
	const fvManifest = useSelector(getFVManifest(fvId ?? ""));
	const modelName = fvManifest?.models.mainDocumentModelId;
	const documentModel = useSelector(ModelSelectors.modelByName(modelName ?? "", Model.isDocumentModel));
	const data = useSelector(ActivitySelectors.data(activityId));
	const contactDataholder = useSelector(
		ActivitySelectors.dataHolderByDescriptor(activityId, { type: "contactData" })
	);
	const contactData = isContactData(contactDataholder?.data) ? contactDataholder?.data : undefined;
	const requestedAppKeys = React.useRef<Set<string>>(new Set());
	const contactDataRef = React.useRef<ContactData | undefined>(contactData);
	if (contactData === undefined && contactDataRef.current !== undefined) {
		requestedAppKeys.current.clear();
	}
	contactDataRef.current = contactData;
	const pendingAppKeysRef = React.useRef<string[]>([]);

	const requestData = React.useCallback(
		(appKeys: string[]) => {
			if (appKeys.every(it => requestedAppKeys.current.has(it))) {
				return;
			}
			const currentContactData = contactDataRef.current;
			const newAppKeys = appKeys.filter(it => !requestedAppKeys.current.has(it));
			if (currentContactData && newAppKeys.every(it => Object.keys(currentContactData.values).includes(it))) {
				newAppKeys.forEach(it => requestedAppKeys.current.add(it));
				return;
			}
			newAppKeys.forEach(it => requestedAppKeys.current.add(it));
			pendingAppKeysRef.current = [
				...pendingAppKeysRef.current,
				...newAppKeys.filter(id => !pendingAppKeysRef.current.includes(id))
			];
		},
		// contactData and dispatch are intentionally excluded: including them would
		// recreate this callback on every data load, causing the form engine to
		// re-invoke the provider and dispatch LOAD_CONTACT_DATA in an infinite loop.
		// contactDataRef.current is used instead to always access the latest value.
		[activityId]
	);

	React.useEffect(() => {
		if (pendingAppKeysRef.current.length > 0) {
			dispatch(LOAD_CONTACT_DATA({ appKeys: pendingAppKeysRef.current, activityId }));
			pendingAppKeysRef.current = [];
		}
	});

	return React.useMemo(
		() =>
			data && FormActivity.Data.SingleDocumentData.isInstance(data) && documentModel
				? createProcessFormEngineEnumProvider(data, documentModel, requestData, contactData)
				: EmptyExternalEnumerationProvider,
		[data, documentModel, requestData, contactData]
	);
}

const CONTACT_LIST_SOURCE = "mailEmpfaenger";

function createProcessFormEngineEnumProvider(
	data: FormActivity.Data.SingleDocumentData,
	model: DocumentModel,
	requestData: (appIds: string[]) => void,
	contactData: ContactData | undefined
): IExternalEnumerationProvider {
	return source => {
		assertTrue(data.document.modelId === model.header.id);
		let adjustedSource = source;
		const externalEnumerationSources: Record<string, { de: string }> = {};
		if (source === CONTACT_LIST_SOURCE || (source.startsWith(CONTACT_LIST_SOURCE) && source.endsWith("_"))) {
			adjustedSource = CONTACT_LIST_SOURCE;
		} else if (source.startsWith(CONTACT_LIST_SOURCE) && !source.endsWith("_")) {
			const appKeys = source.substring(source.indexOf("_") + 1);
			const appKeyList = extractConfigList(appKeys);
			adjustedSource = CONTACT_LIST_SOURCE;
			requestData(appKeyList);
			if (contactData) {
				Object.entries(contactData.values)
					.filter(([key]) => appKeyList.includes(key))
					.forEach(([_, values]) =>
						values.forEach(
							it => (externalEnumerationSources[it.value] = { de: formatContact(it.value, it.label) })
						)
					);
			}
		}

		const annotations = findAnnotations(model, EXTERNAL_ENUMERATION, adjustedSource);
		const paths = Array.from(annotations.keys()).map(e => e.slice(1));

		const externalEnums = Object.values(EXTERNAL_ENUMERATION_SOURCE).includes(adjustedSource)
			? entityInstancePathEnumerationResolver(paths, data)
			: modelPathEnumerationResolver(paths, data);
		Object.assign(externalEnumerationSources, externalEnums);
		if (adjustedSource !== EXTERNAL_ENUMERATION_SOURCE.GENERATED_DOCUMENT) {
			return sortExternalEnumerationSources(externalEnumerationSources);
		}
		const additionalEnums = createProcessFormEngineEnumProvider(
			data,
			model,
			requestData,
			contactData
		)(EXTERNAL_ENUMERATION_SOURCE.FILE_UPLOAD_DATA);
		Object.assign(externalEnumerationSources, additionalEnums);
		return sortExternalEnumerationSources(externalEnumerationSources);
	};
}

function sortExternalEnumerationSources(sources: Record<string, { de: string }>): Record<string, { de: string }> {
	return Object.fromEntries(
		Object.entries(sources).sort(([, a], [, b]) =>
			a.de.localeCompare(b.de, undefined, { sensitivity: "base", numeric: true })
		)
	);
}

function entityInstancePathEnumerationResolver(
	paths: ModelPath[],
	data: FormActivity.Data.SingleDocumentData
): Record<string, { de: string }> {
	const documentService = new DocumentServiceFactory().getDocumentService();

	return Object.fromEntries(
		paths
			.flatMap(it =>
				documentService.getEntityPathsInDocument(data.document, entityInstancePathFromModelPath(it, true))
			)
			.map(e => ({
				path: convertEntityInstancePathToString(e),
				value: documentService.getAssignedObject(data.document, e)
			}))
			.filter(e => e.value)
			.map(e => (isObject(e.value) ? { ...e, value: e.value.original_filename } : e))
			.filter(e => isString(e.value))
			.map(e => [e.path, { de: e.value as string }])
	);
}

function modelPathEnumerationResolver(
	paths: ModelPath[],
	data: FormActivity.Data.SingleDocumentData
): Record<string, { de: string }> {
	return Object.fromEntries(
		paths
			.map(e => ({ path: ModelPath.toString(e), value: getValueFromModelPath(e, data.document) }))
			.filter(e => e.value)
			.map(e => (isObject(e.value) ? { ...e, value: e.value.original_filename } : e))
			.filter(e => isString(e.value))
			.map(e => [e.path, { de: e.value as string }])
	);
}
